import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { ChevronLeft, Loader2, Bell, Copy, Download, Table as TableIcon, Sparkles } from 'lucide-react';
import { toast } from '../hooks/use-toast';

// Sub-components
import AlertSidebar    from '../components/alert/AlertSidebar';
import AlertFilters    from '../components/alert/AlertFilters';
import ListingsDisplay from '../components/alert/ListingsDisplay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCachedFilters = (alertId) => { try { const c = localStorage.getItem(`alert_filters_${alertId}`); return c ? JSON.parse(c) : null; } catch { return null; } };
const setCachedFilters = (alertId, filters) => { try { localStorage.setItem(`alert_filters_${alertId}`, JSON.stringify(filters)); } catch {} };

const AlertDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert]                         = useState(null);
  const [rawListings, setRawListings]             = useState([]);
  const [estimatedHistory, setEstimatedHistory]   = useState([]);
  const [loadingEstimates, setLoadingEstimates]   = useState(true);
  const [loading, setLoading]                     = useState(true);
  const [loadingListings, setLoadingListings]     = useState(false);
  const [refreshing, setRefreshing]               = useState(false);
  const [aiSummary, setAiSummary]                 = useState('');
  const [loadingAI, setLoadingAI]                 = useState(false);
  const [expandedTiers, setExpandedTiers]         = useState({});
  const toggleTier = (idx) => setExpandedTiers(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Filters (with localStorage cache)
  const cached = getCachedFilters(id);
  const [sortBy, setSortBy]                           = useState(cached?.sortBy || 'newest');
  const [priceFilter, setPriceFilter]                 = useState(cached?.priceFilter || 'all');
  const [sellerFilter, setSellerFilter]               = useState(cached?.sellerFilter || 'all');
  const [marketplaceFilter, setMarketplaceFilter]     = useState(cached?.marketplaceFilter || 'all');
  const [conditionFilter, setConditionFilter]         = useState(cached?.conditionFilter || 'working');
  const [sellerSearchOpen, setSellerSearchOpen]       = useState(false);
  const [marketplaceSearchOpen, setMarketplaceSearchOpen] = useState(false);
  const [viewMode, setViewMode]                       = useState(cached?.viewMode || 'list');

  // Config
  const [qualityKeywords, setQualityKeywords] = useState(['as-is', 'as is', 'damaged', 'for parts', 'parts only', 'not working', 'broken', 'cracked', 'faulty', 'defective']);
  const [priceThresholds, setPriceThresholds] = useState({ iphone: 100, ipad: 100, macbook: 200, laptop: 200, airpods: 30, watch: 50, playstation: 100, xbox: 100, switch: 100 });

  // Cache filters on change
  useEffect(() => { setCachedFilters(id, { sortBy, priceFilter, sellerFilter, marketplaceFilter, conditionFilter, viewMode }); }, [id, sortBy, priceFilter, sellerFilter, marketplaceFilter, conditionFilter, viewMode]);

  // Sort raw listings
  const scrapedListings = useMemo(() => {
    const sorted = [...rawListings];
    if (sortBy === 'price-low') sorted.sort((a, b) => (a.min_price || a.price || 0) - (b.min_price || b.price || 0));
    else if (sortBy === 'price-high') sorted.sort((a, b) => (b.max_price || b.price || 0) - (a.max_price || a.price || 0));
    else if (sortBy === 'value') {
      const storage = l => { if (l.variants?.length > 0) { const s = l.variants.map(v => parseInt(v.storage) || 0); return s.reduce((a, b) => a + b, 0) / s.length; } const m = l.title.match(/(\d+)\s*GB/i); return m ? parseInt(m[1]) : 128; };
      sorted.sort((a, b) => { const pa = a.min_price || a.price || 0, pb = b.min_price || b.price || 0; const sa = storage(a), sb = storage(b); return (sa > 0 ? pa / sa : 999) - (sb > 0 ? pb / sb : 999); });
    } else if (sortBy === 'newest') sorted.sort((a, b) => new Date(b.scraped_at) - new Date(a.scraped_at));
    return sorted;
  }, [rawListings, sortBy]);

  // Unique sellers / marketplaces
  const uniqueSellers = useMemo(() => {
    const counts = {}; scrapedListings.forEach(l => { if (l.seller_name?.trim()) counts[l.seller_name] = (counts[l.seller_name] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  }, [scrapedListings]);

  const uniqueMarketplaces = useMemo(() => {
    const counts = {}; scrapedListings.forEach(l => { if (l.marketplace?.trim()) counts[l.marketplace] = (counts[l.marketplace] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])).map(([name, count]) => ({ name, count }));
  }, [scrapedListings]);

  // Data fetching
  useEffect(() => {
    const loadPage = async () => { await fetchAlertData(); fetchEstimatedHistory(); };
    loadPage();
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/alerts/${id}`);
        setAlert(res.data);
        if (res.data?.status === 'running') { const lr = await axios.get(`${API_URL}/api/alerts/${id}/results`); setRawListings(lr.data); }
      } catch {}
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [id]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [kw, thr] = await Promise.all([axios.get(`${API_URL}/api/config/quality-keywords`), axios.get(`${API_URL}/api/config/price-thresholds`)]);
        if (kw.data.keywords?.length > 0) setQualityKeywords(kw.data.keywords);
        if (thr.data?.thresholds) setPriceThresholds(thr.data.thresholds);
      } catch {}
    };
    fetchConfig();
  }, []);

  const fetchEstimatedHistory = async () => {
    setLoadingEstimates(true);
    try { const r = await axios.get(`${API_URL}/api/alerts/${id}/estimated-history`); setEstimatedHistory(r.data.estimated_history || []); } catch {}
    setLoadingEstimates(false);
  };

  const fetchAlertData = async (silentRefresh = false) => {
    if (!silentRefresh && !alert) setLoading(true);
    else if (!silentRefresh) setLoadingListings(true);
    try {
      const alertRes = await axios.get(`${API_URL}/api/alerts/${id}`);
      setAlert(alertRes.data); setLoading(false);
      setLoadingListings(true);
      const listingsRes = await axios.get(`${API_URL}/api/alerts/${id}/results`);
      setRawListings(listingsRes.data); setLoadingListings(false);
    } catch (err) {
      if (err.response?.status === 404) setAlert(null);
      setLoading(false); setLoadingListings(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/api/alerts/${id}/run-now`);
      toast({ title: 'Scraping started', description: 'Results will appear in a few minutes.' });
      setTimeout(() => fetchAlertData(), 3000);
    } catch { toast({ title: 'Error', description: 'Failed to refresh alert', variant: 'destructive' }); }
    setRefreshing(false);
  };

  const handleStopScraping = async () => {
    try {
      await axios.post(`${API_URL}/api/alerts/${id}/stop`);
      toast({ title: 'Scraping stopped', description: 'Alert status updated to inactive.' });
      setTimeout(() => fetchAlertData(), 1000);
    } catch { toast({ title: 'Error', description: 'Failed to stop scraping', variant: 'destructive' }); }
  };

  // Summary data
  const getSummaryData = () => {
    if (scrapedListings.length === 0) return { lowestPrice: 0, highestPrice: 0, averagePrice: 0, representativeImage: null, totalResults: 0, priceDistribution: [] };
    const prices = scrapedListings.map(l => l.price).filter(p => p > 0);
    const lowestPrice = Math.min(...prices), highestPrice = Math.max(...prices);
    const averagePrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const representativeImage = scrapedListings.find(l => l.image_url)?.image_url || null;

    const generateDynamicRanges = (prices, minPrice, maxPrice) => {
      if (prices.length === 0) return [];
      const range = maxPrice - minPrice, avg = prices.reduce((a, b) => a + b, 0) / prices.length;
      const variance = prices.reduce((s, p) => s + Math.pow(p - avg, 2), 0) / prices.length;
      const cv = Math.sqrt(variance) / avg;
      if (prices.length < 15 || cv < 0.2) return [{ label: `All prices ($${Math.floor(minPrice)} - $${Math.ceil(maxPrice)})`, min: minPrice, max: maxPrice + 1 }];
      const bucketCount = prices.length < 30 || cv < 0.35 ? 2 : prices.length < 50 || cv < 0.50 ? 3 : 4;
      const bucketSize = range / bucketCount;
      return Array.from({ length: bucketCount }, (_, i) => {
        const rMin = minPrice + i * bucketSize, rMax = i === bucketCount - 1 ? maxPrice + 1 : minPrice + (i + 1) * bucketSize;
        const label = i === 0 ? `Under $${Math.ceil(rMax)}` : i === bucketCount - 1 ? `Over $${Math.floor(rMin)}` : `$${Math.floor(rMin)} - $${Math.ceil(rMax)}`;
        return { label, min: rMin, max: rMax };
      });
    };

    const dynamicRanges = generateDynamicRanges(prices, lowestPrice, highestPrice);
    return { lowestPrice, highestPrice, averagePrice: averagePrice.toFixed(2), representativeImage, totalResults: scrapedListings.length, priceDistribution: dynamicRanges.map(r => ({ ...r, count: prices.filter(p => p >= r.min && p < r.max).length })) };
  };

  const summary = getSummaryData();
  const mostRecentSessionTime = scrapedListings.length > 0 ? Math.max(...scrapedListings.map(l => l.scrape_session_time ? new Date(l.scrape_session_time).getTime() : 0)) : 0;

  // Filter logic
  const filteredListings = useMemo(() => {
    let filtered = [...scrapedListings];
    if (priceFilter !== 'all') {
      if (priceFilter.startsWith('range_')) {
        const range = summary.priceDistribution[parseInt(priceFilter.split('_')[1])];
        if (range) filtered = filtered.filter(l => { const p = l.min_price || l.price || 0; return p >= range.min && p < range.max; });
      }
    }
    if (sellerFilter !== 'all') filtered = filtered.filter(l => l.seller_name === sellerFilter);
    if (marketplaceFilter !== 'all') filtered = filtered.filter(l => l.marketplace === marketplaceFilter);
    if (conditionFilter === 'working') {
      filtered = filtered.filter(l => {
        if (l.quality_status) return l.quality_status === 'working';
        const title = l.title?.toLowerCase() || '', price = l.min_price || l.price || 0;
        const hasKw = qualityKeywords.some(kw => title.includes(kw));
        let cheap = false;
        for (const [prod, thr] of Object.entries(priceThresholds)) { if (title.includes(prod.toLowerCase())) { cheap = price > 0 && price < thr; break; } }
        return !hasKw && !cheap;
      });
    } else if (conditionFilter === 'damaged') {
      filtered = filtered.filter(l => {
        if (l.quality_status) return l.quality_status !== 'working';
        const title = l.title?.toLowerCase() || '', price = l.min_price || l.price || 0;
        const hasKw = qualityKeywords.some(kw => title.includes(kw));
        let cheap = false;
        for (const [prod, thr] of Object.entries(priceThresholds)) { if (title.includes(prod.toLowerCase())) { cheap = price > 0 && price < thr; break; } }
        return hasKw || cheap;
      });
    }
    if (sortBy === 'price-low') filtered.sort((a, b) => (a.min_price || a.price || 0) - (b.min_price || b.price || 0));
    else if (sortBy === 'price-high') filtered.sort((a, b) => (b.max_price || b.price || 0) - (a.max_price || a.price || 0));
    else if (sortBy === 'newest') filtered.sort((a, b) => new Date(b.scraped_at) - new Date(a.scraped_at));
    return filtered;
  }, [scrapedListings, sortBy, priceFilter, sellerFilter, marketplaceFilter, conditionFilter, qualityKeywords, priceThresholds]);

  // Smart tier grouping
  const groupIntoSmartTiers = (listings) => {
    const withPrices = listings.filter(l => (l.min_price || l.price) > 0);
    if (withPrices.length === 0) return { tiers: [], strategy: 'no_prices' };
    const prices = withPrices.map(l => l.min_price || l.price);
    const sorted = [...prices].sort((a, b) => a - b);
    const min = sorted[0], max = sorted[sorted.length - 1], avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    const cv = Math.sqrt(prices.reduce((s, p) => s + Math.pow(p - avg, 2), 0) / prices.length) / avg;
    if (withPrices.length < 10 || cv < 0.15) return { tiers: [{ name: 'All Results', items: withPrices, icon: '📋', color: 'gray', expanded: true }], strategy: withPrices.length < 10 ? 'too_few_results' : 'low_price_variation', stats: { minPrice: min, maxPrice: max, avgPrice: avg, count: withPrices.length } };
    if (withPrices.length < 30 || cv < 0.30) {
      const median = sorted[Math.floor(sorted.length / 2)];
      return { tiers: [{ name: 'Best Deals (Lower 50%)', items: withPrices.filter(l => (l.min_price || l.price) <= median), icon: '🏆', color: 'green', expanded: true }, { name: 'Higher Prices (Upper 50%)', items: withPrices.filter(l => (l.min_price || l.price) > median), icon: '💎', color: 'gray', expanded: false }], strategy: 'moderate_variation', stats: { minPrice: min, maxPrice: max, avgPrice: avg, median, count: withPrices.length } };
    }
    const lo = sorted[Math.floor(sorted.length / 3)], hi = sorted[Math.floor(sorted.length * 2 / 3)];
    return { tiers: [{ name: 'Best Deals (Lowest 33%)', items: withPrices.filter(l => (l.min_price || l.price) <= lo), icon: '🏆', color: 'green', expanded: true }, { name: 'Medium Prices (Middle 33%)', items: withPrices.filter(l => (l.min_price || l.price) > lo && (l.min_price || l.price) <= hi), icon: '📊', color: 'gray', expanded: false }, { name: 'Higher Prices (Top 33%)', items: withPrices.filter(l => (l.min_price || l.price) > hi), icon: '💎', color: 'gray', expanded: false }], strategy: 'high_variation', stats: { minPrice: min, maxPrice: max, avgPrice: avg, lowThreshold: lo, highThreshold: hi, count: withPrices.length } };
  };

  const tierData = groupIntoSmartTiers(filteredListings);

  const getPriceColor = (price) => { const avg = parseFloat(summary.averagePrice); if (price < avg * 0.8) return 'text-green-600'; if (price < avg * 1.2) return 'text-yellow-600'; return 'text-red-600'; };
  const getPriceBadge = (price) => { const avg = parseFloat(summary.averagePrice); return price < avg * 0.8 ? <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Best Deal</span> : null; };

  const generateAISummary = async () => {
    setLoadingAI(true);
    try { const r = await axios.post(`${API_URL}/api/alerts/${id}/summarize`); setAiSummary(r.data.summary); }
    catch {
      let s = `Found ${summary.totalResults} items for "${alert.query}". Prices range from S$${summary.lowestPrice} to S$${summary.highestPrice}, with an average of S$${summary.averagePrice}. ${summary.lowestPrice < summary.averagePrice * 0.8 ? 'Great deals available - some items are priced significantly below average!' : 'Prices are relatively consistent across listings.'}`;
      if (alert.global_circulation) s += ` 🌍 Global market: ${alert.global_circulation.active_range} units in circulation (${alert.global_circulation.total_sales} total sold).`;
      setAiSummary(s);
    }
    setLoadingAI(false);
  };

  const copyAllLinks = () => { navigator.clipboard.writeText(scrapedListings.map(l => l.url).join('\n')); toast({ title: 'Links copied!', description: `${scrapedListings.length} links copied to clipboard` }); };

  const exportToCSV = () => {
    const headers = ['#', 'Title', 'Price', 'Seller', 'URL', 'Scraped Date'];
    const rows = scrapedListings.map((l, i) => [i + 1, l.title, l.price, l.seller_name || 'N/A', l.url, new Date(l.scraped_at).toLocaleDateString()]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: `alert-${alert.query}-${new Date().toISOString().split('T')[0]}.csv` });
    a.click();
    toast({ title: 'Exported!', description: 'CSV file downloaded' });
  };

  // Loading/error states
  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar /><div className="max-w-7xl mx-auto px-4 py-16 text-center"><Loader2 className="w-12 h-12 animate-spin mx-auto text-red-500" /><p className="mt-4 text-gray-600">Loading alert...</p></div></div>
  );
  if (!alert) return (
    <div className="min-h-screen bg-gray-50"><Navbar /><div className="max-w-7xl mx-auto px-4 py-16 text-center"><h1 className="text-2xl font-bold text-gray-900 mb-4">Alert not found</h1><Button onClick={() => navigate('/alerts')}>Back to Alerts</Button></div></div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/alerts')} className="mb-4 hover:bg-gray-100">
          <ChevronLeft className="w-4 h-4 mr-1" />Back to Alerts
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Representative Image */}
            <div className="relative bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="aspect-square">
                {summary.representativeImage ? (
                  <img src={summary.representativeImage} alt={alert.query} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
                    <Bell className="w-24 h-24 text-red-300 mb-4" />
                    <p className="text-gray-400 text-lg font-medium">{alert.query || 'Alert Summary'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* AI Summary */}
            {scrapedListings.length > 0 && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl shadow-sm p-6 border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center"><Sparkles className="w-5 h-5 mr-2 text-purple-500" />AI Summary</h3>
                  {!aiSummary && <Button onClick={generateAISummary} disabled={loadingAI} size="sm" variant="outline">{loadingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}</Button>}
                </div>
                {aiSummary ? <p className="text-gray-700 leading-relaxed">{aiSummary}</p> : <p className="text-gray-500 text-sm italic">Click "Generate" to get AI-powered insights</p>}
              </div>
            )}

            {/* Price Distribution */}
            {scrapedListings.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Price Distribution</h3>
                <div className="space-y-3">
                  {summary.priceDistribution.map((range, index) => {
                    const maxCount = Math.max(...summary.priceDistribution.map(r => r.count));
                    const pct = maxCount > 0 ? (range.count / maxCount) * 100 : 0;
                    return (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-1"><span className="text-sm text-gray-600">{range.label}</span><span className="text-sm font-semibold text-gray-900">{range.count} items</span></div>
                        <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-red-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            {scrapedListings.length > 0 && (
              <div className="flex space-x-3">
                <Button onClick={copyAllLinks} variant="outline" className="flex-1"><Copy className="w-4 h-4 mr-2" />Copy All Links</Button>
                <Button onClick={exportToCSV} variant="outline" className="flex-1"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
                <Button onClick={() => setViewMode(viewMode === 'list' ? 'table' : 'list')} variant="outline"><TableIcon className="w-4 h-4 mr-2" />{viewMode === 'list' ? 'Table' : 'List'}</Button>
              </div>
            )}

            {/* Filters */}
            {scrapedListings.length > 0 && (
              <AlertFilters
                sortBy={sortBy} setSortBy={setSortBy}
                priceFilter={priceFilter} setPriceFilter={setPriceFilter}
                sellerFilter={sellerFilter} setSellerFilter={setSellerFilter}
                marketplaceFilter={marketplaceFilter} setMarketplaceFilter={setMarketplaceFilter}
                conditionFilter={conditionFilter} setConditionFilter={setConditionFilter}
                sellerSearchOpen={sellerSearchOpen} setSellerSearchOpen={setSellerSearchOpen}
                marketplaceSearchOpen={marketplaceSearchOpen} setMarketplaceSearchOpen={setMarketplaceSearchOpen}
                viewMode={viewMode} setViewMode={setViewMode}
                uniqueSellers={uniqueSellers} uniqueMarketplaces={uniqueMarketplaces}
                summary={summary} scrapedListings={scrapedListings} filteredListings={filteredListings}
              />
            )}

            {/* Listings */}
            <ListingsDisplay
              alert={alert} filteredListings={filteredListings} scrapedListings={scrapedListings}
              viewMode={viewMode} tierData={tierData} expandedTiers={expandedTiers} toggleTier={toggleTier}
              loadingListings={loadingListings} summary={summary} getPriceColor={getPriceColor}
              getPriceBadge={getPriceBadge} mostRecentSessionTime={mostRecentSessionTime}
              handleRefresh={handleRefresh} refreshing={refreshing}
            />
          </div>

          {/* Right Column */}
          <AlertSidebar
            alert={alert} summary={summary} estimatedHistory={estimatedHistory}
            scrapedListings={scrapedListings} loadingEstimates={loadingEstimates}
            refreshing={refreshing} handleRefresh={handleRefresh} handleStopScraping={handleStopScraping}
          />
        </div>
      </div>
    </div>
  );
};

export default AlertDetailPage;
